import {isUndefined, merge} from 'lodash';
import Assessment from '../assessment.js';
import AssessmentResult from '../../../values/AssessmentResult.js';
import {SINGLE_H1_ID, TECHNICAL_SEO_POINTS} from '@axyseo/const/analysis';

/**
 * Assessment to check whether the body of the text contains more than 1 H1s in the body.
 * This assessment doesn't penalize H1 that is not in the very beginning of the body.
 */
class SingleH1Assessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} config The configuration to use.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: SINGLE_H1_ID,
      docUrl: 'https://docs.avada.io/seo-suite-help-center/seo-on-page-audit/checklist#h1-tag',
      fixPosition: 'description',
      ctaType: 'contactUs',
      priority: 'high',
      title: 'H1 tag',
      content: {
        good: 'H1 tag is optimized.',
        improve: 'You should use only one H1 tag',
        bad: 'Missing H1 tag. Add one H1 to clearly define your page`s topic.'
      }
    };

    this.identifier = SINGLE_H1_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Runs the h1 research and based on this returns an assessment result with a score.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   * @param {Researcher}  researcher  The researcher used for calling the research.
   * @returns {AssessmentResult} The assessment result.
   */
  getResult({paper, researcher}) {
    this._h1Count = researcher.getResearch('h1s');
    const assessmentResult = new AssessmentResult({config: this._config});

    const calculatedResult = this.calculateResult(paper);

    if (!isUndefined(calculatedResult)) {
      assessmentResult.setScore(calculatedResult.score);
      assessmentResult.setStatus(calculatedResult.status);
    }

    return assessmentResult;
  }

  /**
   *
   * @param paper
   * @returns {{score: number, status: string}}
   */
  calculateResult(paper) {
    let status = 'bad';
    const htmlString = paper.getText();
    const desHasH1Tag = htmlString.includes('<h1>');

    if (this._h1Count === 1) {
      status = 'good';
    }

    if (this._h1Count === 0 && !desHasH1Tag) {
      status = 'good';
    }

    if (this._h1Count > 1) {
      status = 'bad';
    }

    const score = this.getScore(TECHNICAL_SEO_POINTS, status);

    return {
      score,
      status
    };
  }

  /**
   * Checks whether the paper has a text.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   *
   * @returns {boolean} True when there is text.
   */
  isApplicable(paper) {
    return true
  }
}

export default SingleH1Assessment;
